/** @jsxImportSource @emotion/react */
import { Button, Container, Form, Input, Message, Stack, Text } from "basis";
import { validateAccountNumber, validateBSB } from "library/formValidation";
import { Product } from "library/hooks/types";
import { useEventTracking } from "react-event-tracker";
import { useNavigate } from "react-router-dom";
import { CommonData, PaymentTypes } from "./types";

interface Props {
  bankTransferDetails: {
    accError: boolean;
    accountNum: string;
    bsb: string;
    bsbError: boolean;
    eligible: boolean;
  };
  commonData: CommonData;
  product: Product;
  amount: number;
  paymentMethod?: PaymentTypes;
}

export const BankTransfer = ({
  bankTransferDetails,
  commonData,
  product,
  amount,
  paymentMethod,
}: Props) => {
  const navigate = useNavigate();
  const { trackEvent } = useEventTracking();

  if (!commonData.payinEligible)
    return (
      <Message severity="info-or-minor" bg="transparent">
        Bank transfer is unavailable. You may have used this feature in the last
        5 days.
        <br />
        Please select another payment method to make a payment.
      </Message>
    );

  const initialValues = {
    bsbNumber: bankTransferDetails.bsb || "",
    accountNumber: bankTransferDetails.accountNum || "",
  };

  const initialErrors = {
    bsbNumber: bankTransferDetails.accError || "",
    accountNumber: bankTransferDetails.accError || "",
  };

  const onSubmit = ({ values, errors }) => {
    if (Object.keys(errors).length !== 0) return;

    trackEvent({
      event: {
        category: "button-link",
        action: "internal-link",
        location: "Payment method",
        label: "Next",
      },
    });
    navigate("/pay-in", {
      state: {
        product,
        commonData,
        paymentDetails: {
          bsb: values.bsbNumber,
          accountNum: values.accountNumber,
        },
        paymentMethod: "direct_transfer",
        amount,
        selectedOption: "",
      },
    });
  };

  return (
    <Form
      initialValues={initialValues}
      initialErrors={initialErrors}
      onSubmit={onSubmit}
    >
      <Text>
        Enter your bank details below. Payments will take 3 days to clear.
      </Text>
      <Stack gap="6" margin="6 0">
        <Container width="50%">
          <Input
            name="bsbNumber"
            label="BSB number"
            testId="bsb-number"
            color="white"
            validate={validateBSB}
          />
        </Container>
        <Input
          name="accountNumber"
          label="Account number"
          testId="account-number"
          color="white"
          validate={validateAccountNumber}
        />
      </Stack>
      <Button type="submit" width="160">
        Next
      </Button>
    </Form>
  );
};
